import { Component, OnInit } from '@angular/core';
import { Sidenavaction } from 'src/assets/js/application/sidenavaction';
import FarmSiteModel from 'src/app/services/farmSite.model';
import { FarmSiteService } from 'src/app/services/farmSite.service';
import { CustomerDeviceService } from 'src/app/services/customerDevice.service';
import CustomerDeviceModel from 'src/app/services/customerDevice.model';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';

/**
 * @component TotalDevicesListComponent
 * @selector app-total-devices-list
 * @implements {OnInit}
 *
 * @description
 * The `TotalDevicesListComponent` is responsible for displaying a comprehensive list of all IoT devices
 * associated with the farm sites. It serves as a dashboard view for monitoring device status and counts
 * across the business hierarchy.
 *
 * ## Key Responsibilities:
 * 1. **Data Aggregation**: Fetches farm sites based on user role and aggregates device data.
 * 2. **Role-Based Access**: Adapts data retrieval based on the logged-in user's role (Farmer, Farm Manager, Business Admin, Provider).
 * 3. **Sorting**: Allows users to sort the device list by name or other keys.
 * 4. **Pagination**: Handles pagination for the device list display.
 *
 * ## User Roles & Permissions:
 * - **Farm Category Users**:
 *   - **Business Admin / Farmer / Farm Manager**: Access to devices in their specific farm sites.
 * - **Provider Category Users**:
 *   - **Business Admin**: Access to devices in farm sites associated with the provider business.
 * - **Other Categories**:
 *   - **Employee/Admin**: Access to all farm sites and devices system-wide.
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessCategory`: Business context.
 * - `businessID`: ID of the logged-in user's business.
 * - `loginBusinessName`: Used for display.
 *
 * ## Data Flow:
 * 1. **Initialization**: `ngOnInit` determines user context.
 * 2. **Data Fetching**: Calls appropriate method (`getAllFarmsitesByLoginBusinessID`, etc.) to fetch farm sites.
 * 3. **Device Fetching**: `getAllDevicesList` fetches device counts/details from `CustomerDeviceService`.
 *
 * @service FarmSiteService - For fetching farm site details.
 * @service CustomerDeviceService - For fetching device counts and details.
 * @service CommonMethodsService - For error handling and alerts.
 * @service PreviousRouteService - For navigation history.
 */
@Component({
  selector: 'app-total-devices-list',
  templateUrl: './total-devices-list.component.html',
  styleUrls: ['./total-devices-list.component.scss']
})
export class TotalDevicesListComponent implements OnInit {
  /** @type {Array<FarmSiteModel>} List of farm sites associated with the user. */
  public farmSites: Array<FarmSiteModel>;

  /** @type {FarmSiteModel} Single farm site model (unused in current logic). */
  public farmSite: FarmSiteModel;

  /** @type {Array<CustomerDeviceModel>} List of customer devices fetched from service. */
  public customerDevices: Array<CustomerDeviceModel>;

  /** @type {boolean} Flag for page loading state. */
  pgLoad = false;

  /** @type {string} Role of the logged-in user. */
  loginPersonType: string;

  /** @type {string} Name of the logged-in user's business. */
  loginUserBusinessName: string;

  /** @type {string} Category of the logged-in user's business. */
  public loginUserBusinessCategory: string;

  /** @type {any} List of farm sites for the logged-in business. */
  farmsitesByLoginBusinessId: any;

  /** @type {Array<any>} Temporary array for storing farm site IDs. */
  temparr = new Array();

  /** @type {any} List of farm sites for feed provider login. */
  feedProviderFarmsitesIds: any;

  /** @type {any} List of farm sites for admin login. */
  adminFarmsitesIds: any;

  /** @type {any} Configuration object (unused). */
  config: any;

  /** @type {any} List used for display/sampling (aliased to customerDevices). */
  samplingList: any;

  // sorting pagination
  /** @type {string} Key to sort by. Default 'name'. */
  key: string = 'name';

  /** @type {boolean} Flag for sort direction. */
  reverse: boolean = false;

  /**
   * @method sort
   * @description Sorts the list based on the provided key. Toggles direction.
   * @param {string} key - The property name to sort by.
   */
  sort(key) {
    this.key = key;
    this.reverse = !this.reverse;
  }

  // pagination sorting
  /** @type {number} Current page number. Default 1. */
  currentpagenum: number = 1;

  /** @type {number} Items per page. Default 15. */
  noofitemsPerPage: number = 15;

  /**
   * Constructor for TotalDevicesListComponent.
   * Initializes services.
   *
   * @param {FarmSiteService} farmsiteEntityService - Service for farm site operations.
   * @param {CustomerDeviceService} customerDeviceService - Service for customer device operations.
   * @param {Sidenavaction} sidenavaction - Service for side navigation.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   */
  constructor(public farmsiteEntityService: FarmSiteService, public customerDeviceService: CustomerDeviceService, public sidenavaction: Sidenavaction, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
  }

  /**
   * @lifecycle ngOnInit
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Sets previous route.
   * - Retrieves user context from `localStorage`.
   * - Determines logic flow based on `loginPersonType` and `loginUserBusinessCategory`.
   * - Calls specific methods to fetch farm sites based on role.
   *
   * @throws {Error} Captures and displays errors via `commonMethodsService`.
   */
  ngOnInit() {
    try {
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      // loginperson type details
      this.loginPersonType = localStorage.getItem('loginPersonType');
      // login person Business name
      this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
      this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
      this.temparr = [];
      // get person list based on login businessid
      if ((this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm') {
        this.getAllFarmsitesByLoginBusinessID();
      } else if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider') {
        this.getAllFarmsitesByLoginFeedProviderID();
      } else {
        this.getAllFarmsites();
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getOnLoadCallsData
   * @description Triggers the fetching of device list data. Called after farm sites are loaded.
   * @throws {Error} Captures and displays errors via `commonMethodsService`.
   */
  getOnLoadCallsData() {
    try {
      this.getAllDevicesList();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getAllFarmsitesByLoginBusinessID
   * @async
   * @description Fetches farm sites for the logged-in business (Farmer/Farm Manager/Business Admin).
   * Populates `farmsitesByLoginBusinessId` and `temparr` with IDs.
   * @throws {Error} Captures and displays errors via `commonMethodsService`.
   */
  async getAllFarmsitesByLoginBusinessID() {
    try {
      this.farmSites = await this.farmsiteEntityService.getFarmeSitesByFarmsId(localStorage.businessID);
      this.farmsitesByLoginBusinessId = this.farmSites;
      this.pgLoad = true;
      for (let i = 0; i < this.farmsitesByLoginBusinessId.length; i++) {
        let eachobj = this.farmsitesByLoginBusinessId[i];
        this.temparr.push(eachobj.id);
      }
      this.getOnLoadCallsData();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getAllFarmsitesByLoginFeedProviderID
   * @async
   * @description Fetches farm sites for the logged-in provider business (Business Admin - Provider).
   * Populates `feedProviderFarmsitesIds` and `temparr` with IDs.
   * @throws {Error} Captures and displays errors via `commonMethodsService`.
   */
  async getAllFarmsitesByLoginFeedProviderID() {
    try {
      this.farmSites = await this.farmsiteEntityService.getFarmeSitesBusinessDetailsByProviderId(localStorage.businessID);
      this.feedProviderFarmsitesIds = this.farmSites;
      this.pgLoad = true;
      for (let i = 0; i < this.feedProviderFarmsitesIds.length; i++) {
        let eachobj = this.feedProviderFarmsitesIds[i];
        this.temparr.push(eachobj.id);
      }
      this.getOnLoadCallsData();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getAllFarmsites
   * @async
   * @description Fetches all farm sites in the system (Admin/Employee).
   * Populates `adminFarmsitesIds` and `temparr` with IDs.
   * @throws {Error} Captures and displays errors via `commonMethodsService`.
   */
  async getAllFarmsites() {
    try {
      this.farmSites = await this.farmsiteEntityService.getAllFarmSites();
      this.adminFarmsitesIds = this.farmSites;
      this.pgLoad = true;
      for (let i = 0; i < this.adminFarmsitesIds.length; i++) {
        let eachobj = this.adminFarmsitesIds[i];
        this.temparr.push(eachobj.id);
      }
      this.getOnLoadCallsData();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getAllDevicesList
   * @async
   * @description Fetches the count and details of devices for the current business context.
   * Populates `customerDevices` and `samplingList`.
   * @throws {Error} Captures and displays errors via `commonMethodsService`.
   */
  async getAllDevicesList() {
    try {
      this.pgLoad = false;
      this.customerDevices = await this.customerDeviceService.getDevicesCount(localStorage.businessID);
      if (this.customerDevices !== undefined) {
        this.samplingList = this.customerDevices;
        this.pgLoad = true;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }
}
